import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

const NUM_SECONDS_TO_DISMISS = 5;

export interface CancelPanicModalProps {
  open: boolean;
  theme?: string;
  onAllPressed: () => void;
  onDismiss: () => void;
}

export function CancelPanicModal({
  open,
  theme: themeProp,
  onAllPressed,
  onDismiss,
}: CancelPanicModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [button1Pressed, setButton1Pressed] = useState(false);
  const [button2Pressed, setButton2Pressed] = useState(false);
  const [button3Pressed, setButton3Pressed] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);

  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current != null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimerSeconds(NUM_SECONDS_TO_DISMISS);
    timerIntervalRef.current = setInterval(() => {
      setTimerSeconds((prev) => {
        const next = (prev ?? NUM_SECONDS_TO_DISMISS) - 1;
        if (next <= 0) {
          stopTimer();
          onDismissRef.current();
          return null;
        }
        return next;
      });
    }, 1000);
  }, [stopTimer]);

  const resetState = useCallback(() => {
    setButton1Pressed(false);
    setButton2Pressed(false);
    setButton3Pressed(false);
    resetTimer();
  }, [resetTimer]);

  // Scroll lock and state reset on open/close
  useEffect(() => {
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
      resetState();
    } else {
      document.body.classList.remove('kstc-scroll-lock');
      stopTimer();
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open, resetState, stopTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, [stopTimer]);

  const pressButton = useCallback(
    (n: number) => {
      if (n === 1) {
        setButton1Pressed(true);
        setButton2Pressed(false);
        setButton3Pressed(false);
        resetTimer();
      } else if (n === 2) {
        setButton1Pressed((b1) => {
          if (!b1) {
            // Reset if button 1 not pressed
            setButton2Pressed(false);
            setButton3Pressed(false);
            resetTimer();
            return false;
          }
          setButton2Pressed(true);
          resetTimer();
          return b1;
        });
      } else if (n === 3) {
        setButton1Pressed((b1) => {
          setButton2Pressed((b2) => {
            if (!b1 || !b2) {
              // Reset if buttons 1 and 2 not pressed
              setButton1Pressed(false);
              setButton2Pressed(false);
              setButton3Pressed(false);
              resetTimer();
              return false;
            }
            setButton3Pressed(true);
            stopTimer();
            onAllPressed();
            return b2;
          });
          return b1;
        });
      }
    },
    [resetTimer, stopTimer, onAllPressed]
  );

  const buttonColor = (pressed: boolean): string => {
    return pressed ? '#ff4136' : '#28b62c';
  };

  const timerText = timerSeconds != null && timerSeconds > 0
    ? `Dismissing in ${timerSeconds} seconds.`
    : '';

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop kstc-cancel-panic-backdrop ${theme}`}>
      <div className="kstc-dialog" role="dialog" aria-modal="true">
        <div className="kstc-header">
          <h2 className="kstc-title"><b>Cancel Panic?</b></h2>
        </div>
        <div className="kstc-body" style={{ paddingBottom: 10 }}>
          Press 1, 2, and then 3 to cancel panic call.
        </div>
        <div style={{ textAlign: 'center', paddingBottom: 10 }}>
          <button
            type="button"
            className="panicButtonCancel123Button"
            style={{ backgroundColor: buttonColor(button1Pressed) }}
            onClick={() => pressButton(1)}
          >
            1
          </button>
          <button
            type="button"
            className="panicButtonCancel123Button"
            style={{ backgroundColor: buttonColor(button2Pressed) }}
            onClick={() => pressButton(2)}
          >
            2
          </button>
          <button
            type="button"
            className="panicButtonCancel123Button"
            style={{ backgroundColor: buttonColor(button3Pressed) }}
            onClick={() => pressButton(3)}
          >
            3
          </button>
        </div>
        <div className="kstc-actions">
          <span className="kstc-cancel-panic-timer">{timerText}</span>
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).CancelPanicModalReact = CancelPanicModal;
